import cn from 'classnames';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { TOOL_NAME_TO_ICON_MAPPING, toolsMessages } from '@/shared/config';
import { Icon, Switch, Tooltip } from '@/shared/ui';

import {
  MPRAnalyticsScreen,
  MPRAnnotationTool,
  MPRCommands,
  MPRInputMode,
  MPRPaneLayoutViewer,
  MPRTool,
  MPR_ANNOTATIONS_VISIBILITY_SHORTCUT,
  MPR_ANNOTATION_SHORTCUTS,
  MPR_AXES_SHORTCUT,
  MPR_SHARPNESS_SHORTCUT,
  MPR_TOOLBAR_GROUPS,
  MPR_TOOLS_BY_MODE_MAP,
  MPR_TOOL_ICON_SIZE,
  MPR_TOOL_SHORTCUTS,
  i18n,
} from '../../config';
import { useMPRCommands } from '../../hooks';
import { MPRPaneLayoutControl } from '../MPRPaneLayoutControl';

import styles from './MPRToolsBar.module.scss';

type MPRStandardPlanesControl = {
  isActive: boolean;
  isAvailable: boolean;
  onChange: (isStandardPlanesEnabled: boolean) => void;
};

type MPRToolsBarProps<Tool extends MPRTool> = {
  activeMode: MPRInputMode;
  isAxesVisible: boolean;
  isZoomSynced: boolean;
  screen: MPRAnalyticsScreen;
  tools: Tool[];
  className?: string;
  commands?: MPRCommands<Tool>;
  shortcutsEnabled?: boolean;
  toothID?: string;
  standardPlanesMode?: MPRStandardPlanesControl;
  paneLayout?: MPRPaneLayoutViewer;
  isDisabled?: boolean;
  variant?: 'default' | 'medicalImage';
  onAxesVisibleChange: (isVisible: boolean) => void;
  onToolClick: (tool: Tool) => void;
  onZoomSyncedChange: (isSynced: boolean) => void;
  renderLayout?: (parts: {
    annotationsControl: ReactNode;
    controls: ReactNode;
    shortcutsControl: ReactNode;
    renderToolbar: (options?: { className?: string }) => ReactNode;
  }) => ReactNode;
};

export const MPRToolsBar = <Tool extends MPRTool>(
  props: MPRToolsBarProps<Tool>,
) => {
  const {
    activeMode,
    isAxesVisible,
    isZoomSynced,
    tools,
    className,
    commands,
    screen,
    shortcutsEnabled = false,
    toothID,
    standardPlanesMode,
    paneLayout,
    isDisabled = false,
    variant = 'default',
    renderLayout,
  } = props;

  const { formatMessage } = useIntl();
  const defaultCommands = useMPRCommands(props);
  const toolCommands = commands ?? defaultCommands;
  const annotationCommands = toolCommands.annotations;
  const imageCommands = toolCommands.imageOptions;
  const isImageToolbar = variant === 'medicalImage';
  const iconSize = isImageToolbar ? 32 : MPR_TOOL_ICON_SIZE;
  const controls = [
    ...tools.map((tool) => ({
      id: tool,
      label: formatMessage(i18n.mprControlsDefaultMessages[tool]),
      icon: MPR_TOOLS_BY_MODE_MAP[tool],
      isActive:
        tool === 'contrastBrightnessMode' && imageCommands
          ? imageCommands.activeTool === 'brightnessContrast'
          : !annotationCommands?.activeTool &&
            !imageCommands?.activeTool &&
            activeMode === tool,
      isDisabled:
        isDisabled ||
        (tool === 'contrastBrightnessMode' &&
          Boolean(imageCommands && !imageCommands.canEdit)),
      isToggle: tool !== 'reset',
      shortcut: MPR_TOOL_SHORTCUTS[tool],
      hasSliceNavigation:
        shortcutsEnabled &&
        tool === 'scrollThroughVolumeMode' &&
        Boolean(toolCommands.stepSlice),
      onClick: () => toolCommands.selectTool(tool),
    })),
    ...(imageCommands
      ? [
          {
            id: 'sharpness',
            label: formatMessage(toolsMessages.sharpness),
            icon: TOOL_NAME_TO_ICON_MAPPING.sharpness,
            isActive: imageCommands.activeTool === 'sharpness',
            isDisabled: isDisabled || !imageCommands.canEdit,
            isToggle: true,
            shortcut: MPR_SHARPNESS_SHORTCUT,
            hasSliceNavigation: false,
            onClick: () => imageCommands.selectTool('sharpness'),
          },
        ]
      : []),
    ...(annotationCommands
      ? (Object.keys(MPR_ANNOTATION_SHORTCUTS) as MPRAnnotationTool[]).map(
          (tool) => ({
            id: tool,
            label: formatMessage(toolsMessages[tool]),
            icon: TOOL_NAME_TO_ICON_MAPPING[tool],
            isActive: annotationCommands.activeTool === tool,
            isDisabled: isDisabled || !annotationCommands.canEdit,
            isToggle: true,
            shortcut: MPR_ANNOTATION_SHORTCUTS[tool],
            hasSliceNavigation: false,
            onClick: () => annotationCommands.selectTool(tool),
          }),
        )
      : []),
    ...(toolCommands.download
      ? [
          {
            id: 'download',
            label: formatMessage(toolsMessages.download),
            icon: TOOL_NAME_TO_ICON_MAPPING.download,
            isActive: false,
            isDisabled: toolCommands.download.isDisabled,
            isToggle: false,
            shortcut: undefined,
            hasSliceNavigation: false,
            onClick: toolCommands.download.execute,
          },
        ]
      : []),
  ];
  const axesLabel = formatMessage({
    id: 'mpr.tools.axesVisible',
    defaultMessage: 'Axes',
  });
  const shortcutsLabel = formatMessage({
    id: 'mpr.tools.keyboardShortcuts',
    defaultMessage: 'Keyboard shortcuts',
  });
  const shortcutHelp = [
    ...controls.flatMap((control) =>
      control.shortcut && !control.isDisabled
        ? [{ label: control.label, shortcut: control.shortcut.label }]
        : [],
    ),
    { label: axesLabel, shortcut: MPR_AXES_SHORTCUT.label },
    ...(toolCommands.stepSlice
      ? [
          {
            label: formatMessage(
              i18n.mprControlsDefaultMessages.scrollThroughVolumeMode,
            ),
            shortcut: '↑/↓',
          },
        ]
      : []),
    ...(annotationCommands
      ? [
          {
            label: formatMessage(toolsMessages.hideMarks),
            shortcut: MPR_ANNOTATIONS_VISIBILITY_SHORTCUT.label,
          },
          {
            label: formatMessage({
              id: 'mpr.tools.cancelAnnotation',
              defaultMessage: 'Cancel drawing or selection',
            }),
            shortcut: 'Esc',
          },
          ...(annotationCommands.canEdit
            ? [
                {
                  label: formatMessage({
                    id: 'mpr.tools.deleteSelectedAnnotation',
                    defaultMessage: 'Delete selected annotation',
                  }),
                  shortcut: 'Delete',
                },
              ]
            : []),
        ]
      : []),
  ];

  const toolGroups = MPR_TOOLBAR_GROUPS.map((group) => {
    const buttons = group.flatMap((id) => {
      if (id === 'paneLayout') {
        return paneLayout
          ? [
              <MPRPaneLayoutControl
                key={id}
                screen={screen}
                toothID={toothID}
                viewer={paneLayout}
                isDisabled={isDisabled}
                className={
                  isImageToolbar
                    ? styles.imageToolButton
                    : cn(styles.toolButton, styles.icon)
                }
              />,
            ]
          : [];
      }

      const control = controls.find((control) => control.id === id);

      if (!control) {
        return [];
      }

      const { label, shortcut, hasSliceNavigation } = control;

      return [
        <Tooltip.Primary
          key={id}
          side={isImageToolbar ? 'right' : 'top'}
          content={label}
        >
          <button
            type="button"
            className={cn(
              isImageToolbar ? styles.imageToolButton : styles.toolButton,
              isImageToolbar && control.isActive && styles.active,
            )}
            disabled={control.isDisabled}
            aria-label={label}
            aria-pressed={control.isToggle ? control.isActive : undefined}
            aria-keyshortcuts={
              [
                shortcutsEnabled && shortcut?.label,
                hasSliceNavigation && 'ArrowUp ArrowDown',
              ]
                .filter(Boolean)
                .join(' ') || undefined
            }
            onClick={control.onClick}
          >
            <Icon
              className={cn(
                !isImageToolbar && styles.icon,
                !isImageToolbar && control.isActive && styles.active,
              )}
              size={iconSize}
              name={control.icon}
            />
          </button>
        </Tooltip.Primary>,
      ];
    });

    return buttons.length > 0 ? (
      <div key={group[0]} className={styles.toolGroup}>
        {buttons}
      </div>
    ) : null;
  });
  const zoomSyncControl = activeMode === 'zoomMode' &&
    !annotationCommands?.activeTool &&
    !imageCommands?.activeTool && (
      <Switch
        disabled={isDisabled}
        onChange={toolCommands.toggleZoomSync}
        checked={isZoomSynced}
      >
        {formatMessage({
          id: 'mpr.tools.zoomSynced',
          defaultMessage: 'Zoom synced',
        })}
      </Switch>
    );
  const annotationsControl = annotationCommands && (
    <Switch
      className={cn(isImageToolbar && styles.imageAnnotationsControl)}
      disabled={isDisabled}
      onChange={annotationCommands.toggleVisibility}
      checked={!annotationCommands.isVisible}
    >
      {formatMessage(toolsMessages.hideMarks)}
    </Switch>
  );
  const standardPlanesControl = standardPlanesMode && (
    <Tooltip.Primary
      content={formatMessage({
        id: 'fullHeadMpr.standardPlanesMode.hint',
        defaultMessage:
          'Show the original CT planes without aligning them to the tooth axis.',
      })}
    >
      <Switch
        disabled={isDisabled || !standardPlanesMode.isAvailable}
        onChange={(event) => standardPlanesMode.onChange(event.target.checked)}
        checked={standardPlanesMode.isActive}
      >
        {formatMessage({
          id: 'fullHeadMpr.standardPlanesMode',
          defaultMessage: 'Standard planes',
        })}
      </Switch>
    </Tooltip.Primary>
  );
  const fullscreenElement = document.fullscreenElement;
  const shortcutsControl = shortcutsEnabled && (
    <Tooltip.Primary
      triggerClassName={styles.shortcutHelpTrigger}
      triggerProps={{
        'aria-label': shortcutsLabel,
        role: 'img',
        tabIndex: 0,
      }}
      portalContainer={
        fullscreenElement instanceof HTMLElement
          ? fullscreenElement
          : undefined
      }
      content={
        <div className={styles.shortcutHelpContent}>
          <div>{shortcutsLabel}</div>
          <dl className={styles.shortcutHelp}>
            {shortcutHelp.map(({ label, shortcut }) => (
              <div key={shortcut} className={styles.shortcutHelpRow}>
                <dt>{label}</dt>
                <dd>
                  <kbd>{shortcut}</kbd>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      }
    >
      <Icon name="info" size={28} className={styles.shortcutHelpIcon} />
    </Tooltip.Primary>
  );
  const axesControl = (
    <div className={styles.axesControl}>
      <Switch
        disabled={isDisabled}
        onChange={toolCommands.toggleAxes}
        checked={isAxesVisible}
      >
        {axesLabel}
      </Switch>
    </div>
  );
  const renderToolbar = ({
    className: toolbarClassName,
  }: {
    className?: string;
  } = {}) => (
    <div
      data-component="mpr-tools-bar"
      className={cn(
        isImageToolbar ? styles.imageToolsContainer : styles.toolsContainer,
        isDisabled && styles.disabled,
        className,
        toolbarClassName,
      )}
    >
      {toolGroups}
      {!isImageToolbar && (
        <>
          {zoomSyncControl}
          {axesControl}
          {annotationsControl}
          {standardPlanesControl}
          {shortcutsControl}
        </>
      )}
    </div>
  );

  return renderLayout
    ? renderLayout({
        annotationsControl: isImageToolbar && annotationsControl,
        controls: isImageToolbar && (
          <>
            {zoomSyncControl}
            {axesControl}
            {standardPlanesControl}
          </>
        ),
        shortcutsControl: isImageToolbar && shortcutsControl,
        renderToolbar,
      })
    : renderToolbar();
};
